import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  MessageCircle,
  Plus,
  Send,
  ArrowLeft,
  X,
  Trash2,
  Settings,
  LogOut,
  Users,
  ImagePlus,
  Loader2,
  Image as ImageIcon,
  Reply,
  SendHorizontal,
  Crown,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../services/AuthContext.js";
import "./chats.css";
import { useNavigate, Link } from "react-router-dom";
import {
  listChats,
  createChat,
  getChat,
  updateChat,
  listMessages,
  sendTextMessage,
  genPresignedUploadUrl,
  sendImageMessage,
  removeMessage,
  addMember,
  removeMember,
  exitChat,
  markChatRead,
  uploadChatImageToS3,
} from "../services/chats.js";
import { searchUsers } from "../services/user_info.js";

const default_profile_image_url =
  "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";
const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const MESSAGE_PAGE_SIZE = 50;

const ChatAvatar = ({ src, alt, className }) => {
  const [imgSrc, setImgSrc] = useState(src || default_profile_image_url);

  useEffect(() => {
    setImgSrc(src || default_profile_image_url);
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={() => setImgSrc(default_profile_image_url)}
    />
  );
};

function getOtherMember(chat, currentUserId) {
  if (!chat?.members) return null;
  return chat.members.find((m) => m.user_id && m.user_id !== currentUserId) || null;
}

function getChatDisplayName(chat, currentUserId) {
  if (chat?.type === "group") {
    return chat.name?.trim() || "Grupo";
  }
  const other = getOtherMember(chat, currentUserId);
  return other?.full_name || "Conversa";
}

function getChatThumbnail(chat, currentUserId) {
  if (chat?.type === "group") {
    return chat.thumbnail_view_url || default_profile_image_url;
  }
  const other = getOtherMember(chat, currentUserId);
  return other?.profile_photo || default_profile_image_url;
}

/**
 * Soft-delete placeholder copy from deleted_by.
 * author → writer's own delete; admin → group admin removed someone else's message.
 */
function getDeletedMessageLabel(msg, { currentUserId } = {}) {
  if (!msg?.deleted) return null;
  if (msg.deleted_by === "admin") {
    return "Mensagem apagada pelo administrador";
  }
  // deleted_by "author" (or unknown legacy): distinguish own vs others when we can
  const mine = Boolean(currentUserId && msg.sender_id === currentUserId);
  if (mine) return "Você apagou esta mensagem";
  return "Mensagem apagada";
}

function getLatestMessageBody(msg, currentUserId) {
  if (!msg) return null;
  if (msg.deleted) {
    return getDeletedMessageLabel(msg, { currentUserId });
  }
  if (msg.type === "image") return "Imagem";
  const text = (msg.content || "").trim();
  return text || "Mensagem";
}

/** Preview line for the chats list (group vs direct). */
function getChatListPreview(chat, currentUserId) {
  const msg = chat?.latest_message;
  if (!msg) {
    return { text: "Nenhuma mensagem ainda", mine: null, isImage: false, deleted: false };
  }

  const body = getLatestMessageBody(msg, currentUserId);
  const mine = msg.sender_id === currentUserId;
  const isImage = !msg.deleted && msg.type === "image";
  const deleted = Boolean(msg.deleted);

  if (chat.type === "group") {
    if (deleted) {
      // Deleted placeholders stand alone (no "Nome: …" prefix)
      return { text: body, mine, isImage: false, deleted: true };
    }
    const author = mine ? "Você" : msg.sender_full_name || "Alguém";
    return { text: `${author}: ${body}`, mine, isImage, deleted: false };
  }

  return { text: body, mine, isImage, deleted };
}

/** Whether the current user may soft-delete this message. */
function canDeleteMessage(msg, chat, { userId, amAdmin }) {
  if (!msg || msg.deleted) return false;
  const mine = msg.sender_id === userId;
  if (chat?.type === "direct") return mine;
  if (chat?.type === "group") return mine || Boolean(amAdmin);
  return mine;
}

/** Append a sent message, skipping if a silent poll already inserted it. */
function appendUniqueMessage(prev, msg) {
  if (!msg?.message_id) return prev;
  if (prev.some((m) => m.message_id === msg.message_id)) return prev;
  return [...prev, msg];
}

/**
 * Merge a polled page into local messages without duplicating by message_id.
 * Keeps existing order for already-shown messages; appends truly new ones.
 */
function mergePolledMessages(prev, chronological) {
  const byId = new Map(chronological.map((m) => [m.message_id, m]));
  const prevIds = new Set(prev.map((m) => m.message_id));
  const updated = prev.map((m) =>
    byId.has(m.message_id) ? { ...m, ...byId.get(m.message_id) } : m
  );
  const brandNew = chronological.filter(
    (m) => m?.message_id != null && !prevIds.has(m.message_id)
  );

  if (brandNew.length === 0) {
    const changed = updated.some((m, i) => {
      const before = prev[i];
      return (
        before?.deleted !== m.deleted ||
        before?.deleted_by !== m.deleted_by ||
        before?.content !== m.content ||
        before?.image_url !== m.image_url ||
        before?.type !== m.type ||
        before?.updated_at !== m.updated_at
      );
    });
    return changed ? updated : prev;
  }

  // Dedupe belt-and-suspenders in case prev already had duplicates
  const seen = new Set();
  const merged = [];
  for (const m of [...updated, ...brandNew]) {
    if (m?.message_id == null || seen.has(m.message_id)) continue;
    seen.add(m.message_id);
    merged.push(m);
  }
  return merged;
}

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Display name + profile slug for a message sender in a group chat. */
function getMessageSenderInfo(msg, chat) {
  if (msg?.sender) {
    const name =
      msg.sender.full_name?.trim() || msg.sender.slug?.trim() || null;
    if (name) {
      return { name, slug: msg.sender.slug?.trim() || null };
    }
  }

  const member = (chat?.members || []).find(
    (m) => m.user_id && m.user_id === msg?.sender_id
  );
  if (member) {
    return {
      name: member.full_name?.trim() || member.slug?.trim() || "Participante",
      slug: member.slug?.trim() || null,
    };
  }

  return { name: "Participante", slug: null };
}

function PersonNameLink({ name, slug, className }) {
  if (slug) {
    return (
      <Link to={`/user/${slug}`} className={className} onClick={(e) => e.stopPropagation()}>
        {name}
      </Link>
    );
  }
  return <span className={className}>{name}</span>;
}

function isMemberAdmin(member, chat) {
  return Boolean(
    member &&
      chat?.admin_user_id != null &&
      member.user_pk === chat.admin_user_id
  );
}

function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  busy = false,
  busyLabel = "Aguarde...",
  danger = false,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return createPortal(
    <div
      className="chat-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="chat-confirm-title"
      onClick={() => {
        if (!busy) onCancel?.();
      }}
    >
      <div
        className="chat-confirm-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-confirm-dialog-header">
          <h3 id="chat-confirm-title">{title}</h3>
          <button
            type="button"
            className="chat-confirm-dialog-close"
            onClick={() => {
              if (!busy) onCancel?.();
            }}
            aria-label="Fechar"
            disabled={busy}
          >
            <X size={20} />
          </button>
        </div>
        <div className="chat-confirm-dialog-body">
          <p className="chat-confirm-text">{message}</p>
        </div>
        <div className="chat-confirm-dialog-footer">
          <button
            type="button"
            className="chat-image-cancel-btn"
            onClick={() => onCancel?.()}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? "chat-confirm-delete-btn" : "chat-image-confirm-btn"}
            onClick={() => onConfirm?.()}
            disabled={busy}
          >
            {busy ? (
              <>
                <Loader2 size={16} className="chat-settings-busy-spinner" /> {busyLabel}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ChatMemberRow({
  member,
  chat,
  userId,
  canRemove,
  onRemove,
  removeDisabled,
}) {
  const isAdmin = isMemberAdmin(member, chat);
  const label = `${member.full_name || member.slug || "Usuário"}${
    member.user_id === userId ? " (você)" : ""
  }`;

  return (
    <div className="chat-member-row">
      {member.slug ? (
        <Link to={`/user/${member.slug}`} className="chat-member-avatar-link">
          <ChatAvatar
            src={member.profile_photo}
            alt={member.full_name || "Membro"}
            className="chat-member-avatar"
          />
        </Link>
      ) : (
        <ChatAvatar
          src={member.profile_photo}
          alt={member.full_name || "Membro"}
          className="chat-member-avatar"
        />
      )}
      <PersonNameLink name={label} slug={member.slug} className="chat-member-name" />
      {isAdmin && (
        <span className="chat-member-admin" title="Administrador do grupo">
          <Crown size={16} aria-hidden />
          <span className="chat-sr-only">Administrador do grupo</span>
        </span>
      )}
      {canRemove && member.user_id && member.user_id !== userId && (
        <button
          type="button"
          className="chat-member-remove"
          aria-label="Remover participante"
          onClick={() => onRemove(member.user_id)}
          disabled={removeDisabled}
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
}

function fileExtension(file) {
  const name = file?.name || "";
  const idx = name.lastIndexOf(".");
  if (idx >= 0) return name.slice(idx);
  if (file?.type === "image/png") return ".png";
  if (file?.type === "image/webp") return ".webp";
  if (file?.type === "image/gif") return ".gif";
  return ".jpg";
}

/** Typeahead user picker backed by GET /users/search */
function UserSearchSelect({
  getIdToken,
  excludeUserIds = [],
  placeholder = "Buscar por nome ou slug...",
  onSelect,
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const excludeKey = excludeUserIds.filter(Boolean).join(",");

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const reqId = ++requestIdRef.current;
      try {
        setSearching(true);
        setSearchError(null);
        const token = await getIdToken();
        const data = await searchUsers(token, { q: term, limit: 10 });
        if (reqId !== requestIdRef.current) return;
        const exclude = new Set(excludeKey ? excludeKey.split(",") : []);
        setResults((data.users || []).filter((u) => u.user_id && !exclude.has(u.user_id)));
        setOpen(true);
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        setResults([]);
        setSearchError(err.message || "Erro na busca");
        setOpen(true);
      } finally {
        if (reqId === requestIdRef.current) setSearching(false);
      }
    }, 280);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, getIdToken, excludeKey]);

  const handlePick = (user) => {
    onSelect?.(user);
    setQuery("");
    setResults([]);
    setOpen(false);
    setSearchError(null);
  };

  return (
    <div className="chat-user-search" ref={wrapRef}>
      <input
        className="chat-form-input"
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (results.length > 0 || searchError) setOpen(true);
        }}
      />
      {open && query.trim().length >= 2 && (
        <div className="chat-user-search-dropdown" role="listbox">
          {searching && (
            <div className="chat-user-search-status">Buscando...</div>
          )}
          {!searching && searchError && (
            <div className="chat-user-search-status chat-user-search-error">{searchError}</div>
          )}
          {!searching && !searchError && results.length === 0 && (
            <div className="chat-user-search-status">Nenhum usuário encontrado</div>
          )}
          {!searching &&
            results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                className="chat-user-search-option"
                role="option"
                onClick={() => handlePick(u)}
              >
                <ChatAvatar
                  src={u.profile_photo}
                  alt={u.full_name || u.slug || "Usuário"}
                  className="chat-user-search-avatar"
                />
                <span className="chat-user-search-meta">
                  <span className="chat-user-search-name">{u.full_name || "Usuário"}</span>
                  {u.slug && <span className="chat-user-search-slug">@{u.slug}</span>}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Chat list ---------- */
function Chats() {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState(null); // null | "direct" | "group"
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [pendingMembers, setPendingMembers] = useState([]); // { user_id, full_name, slug, profile_photo }
  const [directUser, setDirectUser] = useState(null); // { user_id, full_name, slug, profile_photo }
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (!user) return;
    setUserId(user.cognitoUser.username);
  }, [user]);

  const fetchChats = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const token = await getIdToken();
      const data = await listChats(token);
      setChats(data);
    } catch (err) {
      console.error("Error fetching chats:", err);
      if (!silent) {
        setError("Não foi possível carregar as conversas");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchChats();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userId) return undefined;
    const intervalId = setInterval(() => {
      fetchChats({ silent: true });
    }, 5000);
    return () => clearInterval(intervalId);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetCreateForm = () => {
    setCreateType(null);
    setFormError(null);
    setGroupName("");
    setGroupDescription("");
    setPendingMembers([]);
    setDirectUser(null);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const openCreateModal = () => {
    resetCreateForm();
    setShowCreateModal(true);
  };

  const handlePickPendingMember = (picked) => {
    setFormError(null);
    if (!picked?.user_id) return;
    if (picked.user_id === userId) {
      setFormError("Você já fará parte do grupo automaticamente.");
      return;
    }
    if (pendingMembers.some((m) => m.user_id === picked.user_id)) {
      setFormError("Este usuário já foi adicionado.");
      return;
    }
    setPendingMembers((prev) => [
      ...prev,
      {
        user_id: picked.user_id,
        full_name: picked.full_name || picked.slug || "Usuário",
        slug: picked.slug || "",
        profile_photo: picked.profile_photo || null,
      },
    ]);
  };

  const handlePickDirectUser = (picked) => {
    setFormError(null);
    if (!picked?.user_id) return;
    if (picked.user_id === userId) {
      setFormError("Você não pode criar uma conversa consigo mesmo.");
      return;
    }
    setDirectUser({
      user_id: picked.user_id,
      full_name: picked.full_name || picked.slug || "Usuário",
      slug: picked.slug || "",
      profile_photo: picked.profile_photo || null,
    });
  };

  const handleCreateChat = async () => {
    if (!createType) return;

    if (createType === "direct") {
      if (!directUser?.user_id) {
        setFormError("Selecione uma pessoa para conversar.");
        return;
      }
      try {
        setCreating(true);
        setFormError(null);
        const token = await getIdToken();
        const chat = await createChat(token, {
          type: "direct",
          other_user_id: directUser.user_id,
        });
        closeCreateModal();
        navigate(`/chat/${chat.code}`);
      } catch (err) {
        console.error("Error creating direct chat:", err);
        setFormError(err.message || "Não foi possível criar a conversa.");
      } finally {
        setCreating(false);
      }
      return;
    }

    if (!groupName.trim()) {
      setFormError("Informe um nome para o grupo.");
      return;
    }
    try {
      setCreating(true);
      setFormError(null);
      const token = await getIdToken();
      const chat = await createChat(token, {
        type: "group",
        name: groupName.trim(),
        description: groupDescription.trim() || null,
        member_user_ids: pendingMembers.map((m) => m.user_id),
      });
      closeCreateModal();
      navigate(`/chat/${chat.code}`);
    } catch (err) {
      console.error("Error creating group:", err);
      setFormError(err.message || "Não foi possível criar o grupo.");
    } finally {
      setCreating(false);
    }
  };

  const modalTitle =
    createType === "direct"
      ? "Nova conversa direta"
      : createType === "group"
        ? "Novo grupo"
        : "Novo chat";

  return (
    <div className="chat-list-container">
      <div className="chat-list-card">
        <div className="chat-list-header">
          <div className="chat-list-heading">
            <h2 className="chat-list-title">
              <MessageCircle size={22} /> Conversas
            </h2>
            <p className="chat-list-subtitle">Mensagens diretas e grupos</p>
          </div>
          <button type="button" className="chat-new-btn" onClick={openCreateModal}>
            <Plus size={16} /> Novo chat
          </button>
        </div>

        {error && <div className="chat-error">{error}</div>}

        <div className="chat-list">
          {loading ? (
            <div className="chat-state-message">Carregando conversas...</div>
          ) : chats.length === 0 ? (
            <div className="chat-state-message">
              Nenhuma conversa ainda. Crie um novo chat ou envie uma mensagem a partir de um perfil.
            </div>
          ) : (
            chats.map((chat) => {
              const title = getChatDisplayName(chat, userId);
              const thumb = getChatThumbnail(chat, userId);
              const preview = getChatListPreview(chat, userId);
              const previewTime =
                chat.latest_message?.created_at || chat.updated_at;
              return (
                <div
                  key={chat.code}
                  className="chat-chat-item"
                  onClick={() => {
                    // Optimistically clear badge; mark-read runs when ChatView opens
                    if (Number(chat.unread_count) > 0) {
                      setChats((prev) =>
                        prev.map((c) =>
                          c.code === chat.code ? { ...c, unread_count: 0 } : c
                        )
                      );
                    }
                    navigate(`/chat/${chat.code}`);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (Number(chat.unread_count) > 0) {
                        setChats((prev) =>
                          prev.map((c) =>
                            c.code === chat.code ? { ...c, unread_count: 0 } : c
                          )
                        );
                      }
                      navigate(`/chat/${chat.code}`);
                    }
                  }}
                >
                  <ChatAvatar src={thumb} alt={title} className="chat-avatar" />
                  <div className="chat-chat-main">
                    <div className="chat-chat-toprow">
                      <span className="chat-chat-name">
                        {title}
                        {chat.type === "group" && (
                          <span className="chat-type-badge">Grupo</span>
                        )}
                      </span>
                      <span className="chat-chat-time">{formatDate(previewTime)}</span>
                    </div>
                    <div className="chat-chat-subrow">
                      <span
                        className={`chat-chat-preview${
                          preview.mine === true
                            ? " chat-chat-preview-mine"
                            : preview.mine === false
                              ? " chat-chat-preview-theirs"
                              : ""
                        }`}
                      >
                        {chat.type === "direct" && preview.mine === true && (
                          <SendHorizontal
                            size={13}
                            className="chat-preview-icon"
                            aria-label="Enviada por você"
                          />
                        )}
                        {chat.type === "direct" && preview.mine === false && (
                          <Reply
                            size={13}
                            className="chat-preview-icon"
                            aria-label="Recebida"
                          />
                        )}
                        {preview.isImage && (
                          <ImageIcon size={13} className="chat-preview-icon" aria-hidden />
                        )}
                        <span className="chat-chat-preview-text">
                          {preview.deleted ? (
                            <em>{preview.text}</em>
                          ) : (
                            preview.text
                          )}
                        </span>
                      </span>
                      {Number(chat.unread_count) > 0 && (
                        <span
                          className="chat-chat-badge"
                          aria-label={`${chat.unread_count} não lidas`}
                        >
                          {Number(chat.unread_count) > 99 ? "99+" : chat.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="chat-image-overlay" role="dialog" aria-modal="true" aria-label="Novo chat">
          <div className="chat-image-modal chat-image-modal--searchable">
            <div className="chat-image-modal-header">
              <h3>{modalTitle}</h3>
              <button
                type="button"
                className="chat-image-modal-close"
                onClick={closeCreateModal}
                aria-label="Fechar"
                disabled={creating}
              >
                <X size={20} />
              </button>
            </div>
            <div
              className="chat-image-modal-body"
              style={{ flexDirection: "column", background: "#fff", gap: 12, overflow: "visible" }}
            >
              {formError && <div className="chat-error" style={{ margin: 0 }}>{formError}</div>}

              {!createType && (
                <div className="chat-create-type-grid">
                  <button
                    type="button"
                    className="chat-create-type-card"
                    onClick={() => {
                      setFormError(null);
                      setCreateType("direct");
                    }}
                  >
                    <MessageCircle size={28} />
                    <strong>Conversa direta</strong>
                    <span>Mensagem particular com uma pessoa</span>
                  </button>
                  <button
                    type="button"
                    className="chat-create-type-card"
                    onClick={() => {
                      setFormError(null);
                      setCreateType("group");
                    }}
                  >
                    <Users size={28} />
                    <strong>Grupo</strong>
                    <span>Conversa com várias pessoas</span>
                  </button>
                </div>
              )}

              {createType === "direct" && (
                <div className="chat-settings-section" style={{ marginBottom: 0 }}>
                  <h4>Com quem você quer conversar?</h4>
                  {directUser ? (
                    <div className="chat-chip-row">
                      <span className="chat-chip">
                        <ChatAvatar
                          src={directUser.profile_photo}
                          alt={directUser.full_name}
                          className="chat-chip-avatar"
                        />
                        {directUser.full_name}
                        <button
                          type="button"
                          className="chat-chip-remove"
                          aria-label="Trocar pessoa"
                          onClick={() => setDirectUser(null)}
                          disabled={creating}
                        >
                          <X size={14} />
                        </button>
                      </span>
                    </div>
                  ) : (
                    <UserSearchSelect
                      getIdToken={getIdToken}
                      excludeUserIds={[userId]}
                      placeholder="Buscar por nome ou slug..."
                      onSelect={handlePickDirectUser}
                      disabled={creating}
                    />
                  )}
                </div>
              )}

              {createType === "group" && (
                <>
                  <label className="chat-settings-section" style={{ marginBottom: 0 }}>
                    <h4>Nome do grupo</h4>
                    <input
                      className="chat-form-input"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      placeholder="Ex.: Equipe elétrica"
                      disabled={creating}
                    />
                  </label>
                  <label className="chat-settings-section" style={{ marginBottom: 0 }}>
                    <h4>Descrição (opcional)</h4>
                    <textarea
                      className="chat-form-input chat-form-textarea"
                      value={groupDescription}
                      onChange={(e) => setGroupDescription(e.target.value)}
                      placeholder="Sobre o que é este grupo?"
                      rows={3}
                      disabled={creating}
                    />
                  </label>
                  <div className="chat-settings-section" style={{ marginBottom: 0 }}>
                    <h4>Participantes</h4>
                    <UserSearchSelect
                      getIdToken={getIdToken}
                      excludeUserIds={[userId, ...pendingMembers.map((m) => m.user_id)]}
                      placeholder="Buscar por nome ou slug..."
                      onSelect={handlePickPendingMember}
                      disabled={creating}
                    />
                    {pendingMembers.length > 0 && (
                      <div className="chat-chip-row">
                        {pendingMembers.map((m) => (
                          <span key={m.user_id} className="chat-chip">
                            {m.full_name}
                            <button
                              type="button"
                              className="chat-chip-remove"
                              aria-label={`Remover ${m.full_name}`}
                              onClick={() =>
                                setPendingMembers((prev) =>
                                  prev.filter((x) => x.user_id !== m.user_id)
                                )
                              }
                              disabled={creating}
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="chat-image-modal-footer">
              {createType ? (
                <button
                  type="button"
                  className="chat-image-cancel-btn"
                  onClick={() => {
                    setFormError(null);
                    setCreateType(null);
                    setDirectUser(null);
                    setGroupName("");
                    setGroupDescription("");
                    setPendingMembers([]);
                  }}
                  disabled={creating}
                >
                  Voltar
                </button>
              ) : (
                <button type="button" className="chat-image-cancel-btn" onClick={closeCreateModal}>
                  Cancelar
                </button>
              )}
              {createType && (
                <button
                  type="button"
                  className="chat-image-confirm-btn"
                  onClick={handleCreateChat}
                  disabled={creating}
                >
                  {creating
                    ? "Criando..."
                    : createType === "direct"
                      ? "Iniciar conversa"
                      : "Criar grupo"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Conversation ---------- */
function ChatView({ chatCode }) {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sendingText, setSendingText] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [settingsError, setSettingsError] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsBusyLabel, setSettingsBusyLabel] = useState("");
  const [messagePendingDelete, setMessagePendingDelete] = useState(null);
  const [deletingMessage, setDeletingMessage] = useState(false);
  const [memberPendingRemove, setMemberPendingRemove] = useState(null);
  const [exitPending, setExitPending] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);

  const beginSettingsBusy = (label) => {
    setSettingsBusy(true);
    setSettingsBusyLabel(label);
    setSettingsError(null);
  };

  const endSettingsBusy = () => {
    setSettingsBusy(false);
    setSettingsBusyLabel("");
  };

  const scrollContainerRef = useRef(null);
  const imageInputRef = useRef(null);
  const thumbInputRef = useRef(null);
  const shouldStickToBottom = useRef(true);
  const messagesRef = useRef([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!user?.cognitoUser) return;
    setUserId(user.cognitoUser.username);
  }, [user]);

  // admin_user_id is site_de_users.id; match against membership user_pk
  const amAdmin = Boolean(
    chat?.members?.find((m) => m.user_id === userId && m.user_pk === chat.admin_user_id)
  );

  const loadChat = async () => {
    if (!chatCode) return;
    try {
      const token = await getIdToken();
      const data = await getChat(token, chatCode);
      setChat(data);
      setEditName(data.name || "");
      setEditDescription(data.description || "");
    } catch (err) {
      console.error("Error loading chat:", err);
      setError(err.message || "Não foi possível carregar a conversa");
    }
  };

  const scrollMessagesToBottom = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  const isNearBottom = (container, threshold = 100) => {
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  const jumpToLatestMessages = () => {
    shouldStickToBottom.current = true;
    setNewMessageCount(0);
    requestAnimationFrame(() => {
      scrollMessagesToBottom();
      requestAnimationFrame(scrollMessagesToBottom);
    });
  };

  const handleMessagesScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (isNearBottom(container)) {
      shouldStickToBottom.current = true;
      setNewMessageCount(0);
    } else {
      shouldStickToBottom.current = false;
    }
  };

  const fetchMessages = async ({ older = false, silent = false } = {}) => {
    if (!chatCode) return;
    try {
      if (older) setLoadingOlder(true);
      else if (!silent) {
        setLoading(true);
        shouldStickToBottom.current = true;
        setNewMessageCount(0);
      }
      if (!silent) setError(null);
      const token = await getIdToken();
      const before = older && messagesRef.current.length
        ? messagesRef.current[0].created_at
        : undefined;
      const page = await listMessages(token, chatCode, {
        limit: MESSAGE_PAGE_SIZE,
        before,
      });
      // API: newest first → chronological for UI
      const chronological = [...page].reverse();
      setHasMore(page.length >= MESSAGE_PAGE_SIZE);
      if (older) {
        const container = scrollContainerRef.current;
        const prevHeight = container?.scrollHeight || 0;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.message_id));
          const merged = [...chronological.filter((m) => !ids.has(m.message_id)), ...prev];
          return merged;
        });
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - prevHeight;
          }
        });
      } else {
        if (silent) {
          const container = scrollContainerRef.current;
          const prevIds = new Set(messagesRef.current.map((m) => m.message_id));
          const brandNew = chronological.filter(
            (m) => m?.message_id != null && !prevIds.has(m.message_id)
          );
          // Notify about others' new messages when the user is not at the bottom
          const incoming = brandNew.filter((m) => m.sender_id !== userId);
          const nearBottom = isNearBottom(container);

          if (nearBottom) {
            shouldStickToBottom.current = true;
            if (incoming.length > 0) setNewMessageCount(0);
          } else if (incoming.length > 0) {
            shouldStickToBottom.current = false;
            setNewMessageCount((count) => count + incoming.length);
          }

          setMessages((prev) => mergePolledMessages(prev, chronological));
        } else {
          shouldStickToBottom.current = true;
          setNewMessageCount(0);
          setMessages(chronological);
        }
        // Mark as read when opening / refreshing the live conversation
        try {
          await markChatRead(token, chatCode);
        } catch (markErr) {
          console.error("Error marking chat as read:", markErr);
        }
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
      if (!silent) {
        setError(err.message || "Não foi possível carregar as mensagens");
      }
    } finally {
      if (!silent) setLoading(false);
      setLoadingOlder(false);
    }
  };

  useEffect(() => {
    if (!chatCode || !userId) return;
    setNewMessageCount(0);
    loadChat();
    fetchMessages();
  }, [chatCode, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chatCode || !userId) return undefined;
    const intervalId = setInterval(() => {
      fetchMessages({ silent: true });
    }, 5000);
    return () => clearInterval(intervalId);
  }, [chatCode, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to latest after messages render (initial load / own send / near-bottom refresh)
  useEffect(() => {
    if (loading || !shouldStickToBottom.current || !scrollContainerRef.current) return;
    requestAnimationFrame(() => {
      scrollMessagesToBottom();
      requestAnimationFrame(scrollMessagesToBottom);
    });
  }, [messages, loading]);

  const handleMessageSubmit = async () => {
    if (!content.trim() || !chatCode || sendingText) return;
    const text = content.trim();
    try {
      setSendingText(true);
      setError(null);
      const token = await getIdToken();
      const msg = await sendTextMessage(token, chatCode, text);
      shouldStickToBottom.current = true;
      setNewMessageCount(0);
      // Dedupe: a silent poll may have already inserted this message
      setMessages((prev) => appendUniqueMessage(prev, msg));
      setContent("");
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err.message || "Não foi possível enviar a mensagem");
    } finally {
      setSendingText(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!sendingText) handleMessageSubmit();
    }
  };

  const openImagePicker = () => imageInputRef.current?.click();

  const handleImageSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Só é permitido enviar arquivos de imagem.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError("A imagem deve ter no máximo 2 MB.");
      return;
    }

    setError(null);
    if (pendingImage?.url) URL.revokeObjectURL(pendingImage.url);
    setPendingImage({ url: URL.createObjectURL(file), file });
  };

  const cancelPendingImage = () => {
    if (pendingImage?.url) URL.revokeObjectURL(pendingImage.url);
    setPendingImage(null);
  };

  const confirmPendingImage = async () => {
    if (!pendingImage?.file || !chatCode) return;
    try {
      setUploadingImage(true);
      setError(null);
      const token = await getIdToken();
      const file = pendingImage.file;
      const { upload_url, object_key } = await genPresignedUploadUrl(token, chatCode, {
        file_type: file.type,
        file_extension: fileExtension(file),
        purpose: "message",
      });
      await uploadChatImageToS3(upload_url, file);
      const msg = await sendImageMessage(token, chatCode, object_key);
      shouldStickToBottom.current = true;
      setNewMessageCount(0);
      // Dedupe: a silent poll may have already inserted this message
      setMessages((prev) => appendUniqueMessage(prev, msg));
      cancelPendingImage();
    } catch (err) {
      console.error("Error sending image:", err);
      setError(err.message || "Não foi possível enviar a imagem");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteMessage = async () => {
    if (!messagePendingDelete || deletingMessage) return;
    const messageId = messagePendingDelete;
    try {
      setDeletingMessage(true);
      const token = await getIdToken();
      const deletedMsg = await removeMessage(token, chatCode, messageId);
      setMessages((prev) =>
        prev.map((m) =>
          m.message_id === messageId
            ? {
                ...m,
                ...(deletedMsg || {}),
                deleted: true,
                content: "",
                image_url: null,
                deleted_by:
                  deletedMsg?.deleted_by ||
                  (m.sender_id === userId ? "author" : "admin"),
              }
            : m
        )
      );
      setMessagePendingDelete(null);
    } catch (err) {
      setError(err.message || "Não foi possível excluir a mensagem");
      setMessagePendingDelete(null);
    } finally {
      setDeletingMessage(false);
    }
  };

  const handleExitChat = async () => {
    if (!exitPending || settingsBusy) return;
    try {
      beginSettingsBusy("Saindo da conversa...");
      const token = await getIdToken();
      await exitChat(token, chatCode);
      setExitPending(false);
      navigate("/chats");
    } catch (err) {
      setSettingsError(err.message || "Não foi possível sair da conversa");
      setExitPending(false);
      endSettingsBusy();
    }
  };

  const handleSaveName = async () => {
    try {
      beginSettingsBusy("Salvando nome...");
      const token = await getIdToken();
      const updated = await updateChat(token, chatCode, { name: editName.trim() || null });
      setChat(updated);
      setEditDescription(updated.description || "");
    } catch (err) {
      setSettingsError(err.message || "Não foi possível atualizar o nome");
    } finally {
      endSettingsBusy();
    }
  };

  const handleSaveDescription = async () => {
    try {
      beginSettingsBusy("Salvando descrição...");
      const token = await getIdToken();
      const updated = await updateChat(token, chatCode, {
        description: editDescription.trim() || null,
      });
      setChat(updated);
      setEditDescription(updated.description || "");
    } catch (err) {
      setSettingsError(err.message || "Não foi possível atualizar a descrição");
    } finally {
      endSettingsBusy();
    }
  };

  const handleThumbnailSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSettingsError("Só é permitido enviar arquivos de imagem.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setSettingsError("A imagem deve ter no máximo 2 MB.");
      return;
    }
    try {
      beginSettingsBusy("Enviando imagem do grupo...");
      const token = await getIdToken();
      const { upload_url, object_key } = await genPresignedUploadUrl(token, chatCode, {
        file_type: file.type,
        file_extension: fileExtension(file),
        purpose: "thumbnail",
      });
      await uploadChatImageToS3(upload_url, file);
      const updated = await updateChat(token, chatCode, { thumbnail_url: object_key });
      setChat(updated);
    } catch (err) {
      setSettingsError(err.message || "Não foi possível atualizar a imagem do grupo");
    } finally {
      endSettingsBusy();
    }
  };

  const handleAddMemberFromSearch = async (picked) => {
    if (!picked?.user_id) return;
    try {
      beginSettingsBusy(`Adicionando ${picked.full_name || "participante"}...`);
      const token = await getIdToken();
      const updated = await addMember(token, chatCode, picked.user_id);
      setChat(updated);
    } catch (err) {
      setSettingsError(err.message || "Não foi possível adicionar o participante");
    } finally {
      endSettingsBusy();
    }
  };

  const handleRemoveMember = async () => {
    if (!memberPendingRemove || settingsBusy) return;
    const memberUserId = memberPendingRemove;
    try {
      beginSettingsBusy("Removendo participante...");
      const token = await getIdToken();
      const updated = await removeMember(token, chatCode, memberUserId);
      setChat(updated);
      setMemberPendingRemove(null);
    } catch (err) {
      setSettingsError(err.message || "Não foi possível remover o participante");
      setMemberPendingRemove(null);
    } finally {
      endSettingsBusy();
    }
  };

  if (!chatCode) {
    return (
      <div className="chat-chat-container">
        <div className="chat-conversation">
          <div className="chat-empty">
            <MessageCircle size={64} className="chat-empty-icon" />
            <p className="chat-empty-text">Selecione uma conversa para começar.</p>
          </div>
        </div>
      </div>
    );
  }

  const title = chat ? getChatDisplayName(chat, userId) : "Carregando...";
  const thumb = chat ? getChatThumbnail(chat, userId) : default_profile_image_url;
  const canManageGroup = amAdmin && chat?.type === "group";
  const otherMember = chat?.type === "direct" ? getOtherMember(chat, userId) : null;
  const headerProfileSlug = otherMember?.slug?.trim() || null;

  return (
    <div className="chat-chat-container">
      <div className="chat-conversation">
        <div className="chat-conversation-header">
          <a href="/chats" className="chat-back" aria-label="Voltar para conversas">
            <ArrowLeft size={20} />
          </a>
          {headerProfileSlug ? (
            <Link to={`/user/${headerProfileSlug}`} className="chat-header-avatar-link">
              <ChatAvatar src={thumb} alt={title} className="chat-header-avatar" />
            </Link>
          ) : (
            <ChatAvatar src={thumb} alt={title} className="chat-header-avatar" />
          )}
          <div className="chat-header-info">
            <PersonNameLink
              name={title}
              slug={headerProfileSlug}
              className="chat-header-name"
            />
            {chat?.type === "group" && chat.description?.trim() ? (
              <span className="chat-header-description" title={chat.description}>
                {chat.description}
              </span>
            ) : (
              <a href="/chats" className="chat-header-link">Ver todas as conversas</a>
            )}
          </div>
          <div className="chat-header-actions">
            <button
              type="button"
              className="chat-header-icon-btn"
              aria-label="Configurações"
              onClick={() => {
                setSettingsOpen(true);
                setSettingsError(null);
                setEditName(chat?.name || "");
                setEditDescription(chat?.description || "");
              }}
            >
              <Settings size={18} />
            </button>
          </div>
        </div>

        <div className="chat-messages-wrap">
          <div
            className="chat-messages"
            ref={scrollContainerRef}
            onScroll={handleMessagesScroll}
          >
          {error && <div className="chat-error">{error}</div>}

          {hasMore && (
            <button
              type="button"
              className="chat-load-older"
              disabled={loadingOlder}
              onClick={() => {
                shouldStickToBottom.current = false;
                fetchMessages({ older: true });
              }}
            >
              {loadingOlder ? "Carregando..." : "Carregar mensagens anteriores"}
            </button>
          )}

          {loading ? (
            <div className="chat-loading">Carregando mensagens...</div>
          ) : messages.length === 0 ? (
            <div className="chat-empty">
              <MessageCircle size={48} className="chat-empty-icon" />
              <p className="chat-empty-text">Sem mensagens ainda. Seja o primeiro a escrever!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const mine = msg.sender_id === userId;
              const isDeleted = Boolean(msg.deleted);
              const isImage = !isDeleted && msg.type === "image" && msg.image_url;
              const canDelete = canDeleteMessage(msg, chat, { userId, amAdmin });
              const deletedLabel = isDeleted
                ? getDeletedMessageLabel(msg, { currentUserId: userId })
                : null;
              const showSenderName = !mine && chat?.type === "group";
              const senderInfo = showSenderName ? getMessageSenderInfo(msg, chat) : null;
              const bubble = (
                <div
                  className={
                    mine
                      ? `chat-msg chat-msg-sent${isImage ? " chat-msg-image-bubble" : ""}${
                          isDeleted ? " chat-msg-deleted" : ""
                        }`
                      : `chat-msg chat-msg-received${isImage ? " chat-msg-image-bubble" : ""}${
                          isDeleted ? " chat-msg-deleted" : ""
                        }`
                  }
                >
                  {isDeleted ? (
                    <p className="chat-msg-deleted-text">{deletedLabel}</p>
                  ) : isImage ? (
                    <button
                      type="button"
                      className="chat-msg-image-btn"
                      onClick={() => setLightboxUrl(msg.image_url)}
                      aria-label="Ampliar imagem"
                    >
                      <img src={msg.image_url} alt="Imagem enviada" className="chat-msg-image" />
                    </button>
                  ) : (
                    <p className="chat-msg-text">{msg.content}</p>
                  )}
                  <div className="chat-msg-meta">
                    <span className="chat-msg-time">{formatDate(msg.created_at)}</span>
                    {canDelete && (
                      <button
                        type="button"
                        className="chat-msg-delete"
                        aria-label="Excluir mensagem"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setMessagePendingDelete(msg.message_id);
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );

              if (showSenderName && senderInfo) {
                return (
                  <div key={msg.message_id} className="chat-msg-group chat-msg-group-received">
                    <PersonNameLink
                      name={senderInfo.name}
                      slug={senderInfo.slug}
                      className="chat-msg-sender"
                    />
                    {bubble}
                  </div>
                );
              }

              return (
                <React.Fragment key={msg.message_id}>{bubble}</React.Fragment>
              );
            })
          )}
          </div>

          {newMessageCount > 0 && (
            <button
              type="button"
              className="chat-new-messages-fab"
              onClick={jumpToLatestMessages}
              aria-label={
                newMessageCount === 1
                  ? "1 nova mensagem"
                  : `${newMessageCount} novas mensagens`
              }
            >
              <ChevronDown size={18} aria-hidden />
              <span className="chat-new-messages-count">
                {newMessageCount > 99 ? "99+" : newMessageCount}
              </span>
            </button>
          )}
        </div>

        <div className="chat-composer">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite a sua mensagem..."
            className="chat-input"
            rows="1"
            disabled={sendingText}
          />
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="chat-image-input"
            onChange={handleImageSelected}
          />
          <button
            type="button"
            onClick={openImagePicker}
            className="chat-icon-btn chat-attach-btn"
            aria-label="Enviar imagem"
            disabled={sendingText}
          >
            <Plus size={18} />
          </button>
          <button
            type="button"
            onClick={handleMessageSubmit}
            className={`chat-icon-btn chat-send-btn${sendingText ? " chat-send-btn-busy" : ""}`}
            aria-label={sendingText ? "Enviando..." : "Enviar"}
            disabled={sendingText || !content.trim()}
          >
            {sendingText ? <Loader2 size={18} className="chat-send-spinner" /> : <Send size={18} />}
          </button>
        </div>
      </div>

      {pendingImage && (
        <div className="chat-image-overlay" role="dialog" aria-modal="true" aria-label="Confirmar imagem">
          <div className="chat-image-modal">
            <div className="chat-image-modal-header">
              <h3>Confirmar imagem</h3>
              <button
                type="button"
                className="chat-image-modal-close"
                onClick={cancelPendingImage}
                aria-label="Fechar"
                disabled={uploadingImage}
              >
                <X size={20} />
              </button>
            </div>
            <div className="chat-image-modal-body">
              <img src={pendingImage.url} alt="Pré-visualização" className="chat-image-preview" />
            </div>
            <div className="chat-image-modal-footer">
              <button
                type="button"
                className="chat-image-cancel-btn"
                onClick={cancelPendingImage}
                disabled={uploadingImage}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="chat-image-confirm-btn"
                onClick={confirmPendingImage}
                disabled={uploadingImage}
              >
                {uploadingImage ? "Enviando..." : "Enviar imagem"}
              </button>
            </div>
          </div>
        </div>
      )}

      {lightboxUrl && (
        <div
          className="chat-lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Imagem ampliada"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            className="chat-lightbox-close"
            onClick={() => setLightboxUrl(null)}
            aria-label="Fechar"
          >
            <X size={24} />
          </button>
          <img
            src={lightboxUrl}
            alt="Imagem ampliada"
            className="chat-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {settingsOpen && (
        <div className="chat-image-overlay" role="dialog" aria-modal="true" aria-label="Configurações da conversa">
          <div className="chat-image-modal chat-image-modal--searchable">
            <div className="chat-image-modal-header">
              <h3>Configurações</h3>
              <button
                type="button"
                className="chat-image-modal-close"
                onClick={() => {
                  if (settingsBusy) return;
                  setSettingsOpen(false);
                }}
                aria-label="Fechar"
                disabled={settingsBusy}
              >
                <X size={20} />
              </button>
            </div>
            <div
              className={`chat-image-modal-body${settingsBusy ? " chat-settings-body-busy" : ""}`}
              style={{
                flexDirection: "column",
                background: "#fff",
                alignItems: "stretch",
                overflow: "visible",
              }}
            >
              {settingsBusy && (
                <div className="chat-settings-busy-banner" role="status" aria-live="polite">
                  <Loader2 size={18} className="chat-settings-busy-spinner" />
                  <span>{settingsBusyLabel || "Processando..."}</span>
                </div>
              )}

              {settingsError && <div className="chat-error" style={{ margin: 0 }}>{settingsError}</div>}

              {canManageGroup && (
                <>
                  <div className="chat-settings-section">
                    <h4>Nome do grupo</h4>
                    <div className="chat-slug-row">
                      <input
                        className="chat-form-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Nome do grupo"
                        disabled={settingsBusy}
                      />
                      <button
                        type="button"
                        className="chat-btn chat-btn-primary"
                        onClick={handleSaveName}
                        disabled={settingsBusy}
                      >
                        {settingsBusy && settingsBusyLabel.startsWith("Salvando nome") ? (
                          <>
                            <Loader2 size={14} className="chat-settings-busy-spinner" /> Salvando...
                          </>
                        ) : (
                          "Salvar"
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="chat-settings-section">
                    <h4>Descrição</h4>
                    <textarea
                      className="chat-form-input chat-form-textarea"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Sobre o que é este grupo?"
                      rows={3}
                      disabled={settingsBusy}
                    />
                    <button
                      type="button"
                      className="chat-btn chat-btn-primary"
                      onClick={handleSaveDescription}
                      disabled={settingsBusy}
                      style={{ alignSelf: "flex-end", marginTop: 8 }}
                    >
                      {settingsBusy && settingsBusyLabel.includes("descrição") ? (
                        <>
                          <Loader2 size={14} className="chat-settings-busy-spinner" /> Salvando...
                        </>
                      ) : (
                        "Salvar descrição"
                      )}
                    </button>
                  </div>

                  <div className="chat-settings-section">
                    <h4>Imagem do grupo</h4>
                    <input
                      ref={thumbInputRef}
                      type="file"
                      accept="image/*"
                      className="chat-image-input"
                      onChange={handleThumbnailSelected}
                      disabled={settingsBusy}
                    />
                    <button
                      type="button"
                      className="chat-btn chat-btn-secondary"
                      onClick={() => thumbInputRef.current?.click()}
                      disabled={settingsBusy}
                    >
                      {settingsBusy && settingsBusyLabel.includes("imagem") ? (
                        <>
                          <Loader2 size={16} className="chat-settings-busy-spinner" /> Enviando...
                        </>
                      ) : (
                        <>
                          <ImagePlus size={16} /> Alterar imagem
                        </>
                      )}
                    </button>
                  </div>

                  <div className="chat-settings-section">
                    <h4>
                      <Users size={14} style={{ marginRight: 6 }} />
                      Participantes
                    </h4>
                    {(chat.members || []).map((m) => (
                      <ChatMemberRow
                        key={m.user_id || m.id}
                        member={m}
                        chat={chat}
                        userId={userId}
                        canRemove
                        onRemove={setMemberPendingRemove}
                        removeDisabled={settingsBusy}
                      />
                    ))}
                    <UserSearchSelect
                      getIdToken={getIdToken}
                      excludeUserIds={[
                        userId,
                        ...(chat.members || []).map((m) => m.user_id),
                      ]}
                      placeholder="Buscar por nome ou slug..."
                      onSelect={handleAddMemberFromSearch}
                      disabled={settingsBusy}
                    />
                  </div>
                </>
              )}

              {chat?.type === "group" && !canManageGroup && (
                <div className="chat-settings-section">
                  {chat.description?.trim() && (
                    <>
                      <h4>Descrição</h4>
                      <p className="chat-group-description-readonly">{chat.description}</p>
                    </>
                  )}
                  <h4>Participantes</h4>
                  {(chat.members || []).map((m) => (
                    <ChatMemberRow
                      key={m.user_id || m.id}
                      member={m}
                      chat={chat}
                      userId={userId}
                      canRemove={false}
                    />
                  ))}
                </div>
              )}

              <button
                type="button"
                className="chat-danger-btn"
                onClick={() => setExitPending(true)}
                disabled={settingsBusy}
              >
                {settingsBusy && settingsBusyLabel.startsWith("Saindo") ? (
                  <>
                    <Loader2 size={16} className="chat-settings-busy-spinner" style={{ marginRight: 6, verticalAlign: "middle" }} />
                    Saindo...
                  </>
                ) : (
                  <>
                    <LogOut size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
                    Sair da conversa
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={messagePendingDelete != null}
        title="Excluir mensagem"
        message="Tem certeza de que deseja excluir esta mensagem? Ela será removida para todos os participantes."
        confirmLabel="Excluir"
        busy={deletingMessage}
        busyLabel="Excluindo..."
        danger
        onConfirm={handleDeleteMessage}
        onCancel={() => {
          if (!deletingMessage) setMessagePendingDelete(null);
        }}
      />

      <ConfirmModal
        open={memberPendingRemove != null}
        title="Remover participante"
        message="Tem certeza de que deseja remover este participante do grupo?"
        confirmLabel="Remover"
        busy={Boolean(settingsBusy && settingsBusyLabel.startsWith("Removendo"))}
        busyLabel="Removendo..."
        danger
        onConfirm={handleRemoveMember}
        onCancel={() => {
          if (!settingsBusy) setMemberPendingRemove(null);
        }}
      />

      <ConfirmModal
        open={exitPending}
        title="Sair da conversa"
        message="Tem certeza de que deseja sair desta conversa?"
        confirmLabel="Sair"
        busy={Boolean(settingsBusy && settingsBusyLabel.startsWith("Saindo"))}
        busyLabel="Saindo..."
        danger
        onConfirm={handleExitChat}
        onCancel={() => {
          if (!settingsBusy) setExitPending(false);
        }}
      />
    </div>
  );
}

export { Chats, ChatView };
